import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ColumnSelector, { IAvailableColumn } from './ColumnSelector';
import FilterString from './FilterString';
import FilterBoolean from './FilterBoolean';
import FilterObject from './FilterObject';
import FilterEnum from './FilterEnum';
import FilterDate from './FilterDate';
import FilterDateTime from './FilterDateTime';
import FilterLong from './FilterLong';
import FilterBigDecimal from './FilterBigDecimal';
import { viewActions } from '../../state/modules/view';
import * as ViewHelper from '../../utils/ViewHelper';
import * as entitiesImport from '../../graphql/entities';
import btnStyles from '../../styles/partials/_button.css';
import componentStyles from '../../styles/partials/_filterscontainer.css';
import fieldMetaData, { IEnumMeta, IEnumMetaData } from '../../graphql/schema/fieldMetaData';
import styled from 'styled-components';
import getIn from '../connectedForm/utils/getIn';
import { useDispatch } from 'react-redux';
import ViewContext from '../Context/ViewContext';
// @ts-ignore
import { Analytics } from 'mixpanel';
import { FILTER_TYPES } from '../../utils/FilterHelper';
import FilterTable from './FilterTable';
import WorkspaceContext from '../Context/WorkspaceContext';
import { EntityType } from '../../@types/entityTypes';
import { Dispatch } from 'redux';
import { Maybe } from '../../graphql/schema/p4types';

const entities = entitiesImport as unknown as { [key: string]: EntityType | undefined };

const styles = { ...componentStyles, btnStyles };

export const SelectedStatusContainer = styled.div`
  display: flex;
  align-items: center;
`;
export const SelectedStatus = styled.div<{ backgroundColor?: string }>`
  display: flex;
  font-weight: 600;
  padding: 2px 7px 2px 7px;
  margin-left: 10px;
  font-size: 12px;
  color: #fff;
  background-color: ${({ backgroundColor }) => backgroundColor || '#00b9af'};
  border-radius: 2px;
`;

export const CustomActionContainer = styled.div`
  padding-left: 12px;
  display: flex;
  align-items: center;
`;

const useFilterColumns = (availableColumns: Array<IAvailableColumn>) => {
  return useMemo(() => {
    return availableColumns.map((column) => {
      const { objectType } = column;
      if (objectType && (entities as unknown as { [key: string]: EntityType | undefined })[objectType]?.dropdown) {
        return { ...column, dropdown: entities[objectType]?.dropdown };
      }
      return column;
    });
  }, [availableColumns]);
};

const Filter = ({
  filter,
  filterType,
  tabIndex,
  filterColumns,
  handleFilterUpdated,
  dismissFilter,
}: {
  filter: { fieldName: string };
  filterType: unknown;
  tabIndex: number;
  filterColumns: Array<any>;
  handleFilterUpdated?: (filter: { fieldName: string }, newFilter: unknown) => void;
  dismissFilter?: (filter: { fieldName: string }) => void;
}) => {
  const { currentView } = useContext(ViewContext);
  const dispatch = useDispatch();

  return useMemo(() => {
    const props: {
      key: string;
      onFilterUpdated?: (filter: { fieldName: string }, newFilter: unknown) => void;
      label: string;
      type: unknown;
      currentViewName?: string;
      tabIndex?: number;
      dismissFilter?: (filter: { fieldName: string }) => void;
      enumValues?: Array<IEnumMeta<any>>;
      'data-testid': string;
      testid: string; //Backwards compatability, use data-testid by preference.
      dispatch: Dispatch;
      objectType?: string;
      objects?: Array<any>;
      codes?: Array<string>;
    } = Object.assign({}, filter) as any;
    props.key = filter.fieldName;
    props.onFilterUpdated = handleFilterUpdated;
    const fieldColumn = filterColumns.find((column) => {
      return column.fieldName === filter.fieldName;
    });

    props.label = fieldColumn ? fieldColumn.title : 'Unknown field';
    props.type = filterType;
    props.currentViewName = currentView?.name;
    props.tabIndex = tabIndex;
    props.dismissFilter = dismissFilter;
    props.enumValues = fieldColumn && fieldColumn.options && fieldColumn.options.enumValues;
    props['data-testid'] = `filter_${filter.fieldName}`;
    props.testid = props['data-testid']; //Here for compatibility - Expected use is data-testid
    props.dispatch = dispatch; //Restore 'alerts' for when a filter is invalid (Using notification system instead).

    switch (filterType) {
      case 'filterStrings':
      case 'filterAttributeStrings':
        return <FilterString {...props} fieldColumn={fieldColumn} />;
      case 'filterBooleans':
        return <FilterBoolean {...props} fieldColumn={fieldColumn} />;
      case 'filterEnums':
        props.objectType = fieldColumn.type;
        // @ts-ignore
        return <FilterEnum {...props} />;
      case 'filterObjects':
        if (fieldColumn && fieldColumn.type === 'Object' && fieldColumn.dropdown) {
          const dropdownProps = Object.assign(props, fieldColumn.dropdown);
          if (!props.objects && props.codes) {
            props.objects = props.codes.map((code) => {
              return { id: code };
            });
          }
          return <FilterTable {...dropdownProps} fieldColumn={fieldColumn} />;
        }
        console.warn('Set to Filter Objects, but not configured to do so.', { fieldColumn });
        return null;
      case 'filterAttributeObjects':
        const dropdownProps = {
          ...props,
          labelKey: 'description',
          valueKey: 'id',
          options: getIn(fieldColumn, 'options.attributeConfig.attributeReferenceList'),
        };
        if (dropdownProps.objects) {
          dropdownProps.objects = dropdownProps.objects.map((option) => {
            if (dropdownProps.options && Array.isArray(dropdownProps.options)) {
              return dropdownProps.options.find((opt) => opt.id === option.id);
            }
            return option;
          });
        } else {
          dropdownProps.objects = [];
        }
        return <FilterObject {...dropdownProps} />;
      case 'filterDates':
      case 'filterAttributeDates':
        // @ts-ignore
        return <FilterDate {...props} />;
      case 'filterDateTimes':
        return <FilterDateTime {...props} />;
      case 'filterLongs':
      case 'filterAttributeLongs':
        return <FilterLong {...props} />;
      case 'filterBigDecimals':
      case 'filterAttributeBigDecimals':
        return <FilterBigDecimal {...props} fieldColumn={fieldColumn} />;
      default:
        console.warn('Filter set, but no matching FilterX element could be found');
        return null;
    }
  }, [currentView?.name, dismissFilter, dispatch, filter, filterColumns, filterType, handleFilterUpdated, tabIndex]);
};

type IFiltersContainerProps = {
  screenName: string;
  availableColumns: Array<IAvailableColumn>;
  tabIndex: number; //TODO - Required?
  preventSelectionResetOnFilterChange?: boolean;
};

type ICurrentViewFilter = {
  fieldName: string;
  type: string;
  filterStyle: string;
  label?: string;
};

const FiltersContainer = (props: IFiltersContainerProps) => {
  const { currentView } = useContext(ViewContext);
  const { workspaceSelection } = useContext(WorkspaceContext);
  const queryFilters = useRef<{ [key: string]: any }>({});
  const [lastAddedFilter, setLastAddedFilter] = useState<Maybe<string>>(null);
  const dispatch = useDispatch();

  const { screenName } = props;

  const fieldNameToColumnMap = useMemo(
    () => ViewHelper.getFieldNameToColumnMap(props.availableColumns),
    [props.availableColumns]
  );

  useEffect(() => {
    queryFilters.current = {}; //Clear queryFilters if ViewName changes.
  }, [currentView?.name]);

  const getActiveFilter = (filterType: string, fieldName: string) => {
    const tableView = currentView?.[filterType];
    if (!tableView) {
      return null;
    }

    return tableView.find((filter: { fieldName: string }) => {
      return filter.fieldName === fieldName;
    });
  };

  const dismissFilter = useCallback(
    (filter) => {
      delete queryFilters.current[filter.fieldName];
      dispatch(viewActions.updateFilters(props.screenName, [], [filter]));
    },
    [dispatch, props.screenName]
  );

  const onUpdateColumns = (
    columns: Array<{ fieldName: string; selected?: boolean; filterStyle?: string; label?: string }>
  ) => {
    let dataType: keyof typeof ViewHelper.FILTER_TYPE_MAP;
    let filter: ICurrentViewFilter;

    const addFilters: Array<typeof filter> = [];
    const removeFilters: Array<typeof filter> = [];
    const updateFilters: Array<typeof filter> = [];
    let filterType;
    columns.forEach((column) => {
      dataType = fieldNameToColumnMap[column.fieldName].type as keyof typeof ViewHelper.FILTER_TYPE_MAP;
      filterType = ViewHelper.FILTER_TYPE_MAP[dataType] || '';
      if (filterType === '') {
        const fieldMeta = fieldMetaData[dataType] as Maybe<IEnumMetaData>;
        if (fieldMeta && fieldMeta.enumValues) filterType = 'filterEnums';
      }
      filter = getActiveFilter(filterType, column.fieldName);

      if (!column.selected !== !filter) {
        filter = {
          fieldName: column.fieldName,
          type: filterType,
          filterStyle: column.filterStyle || 'DEFAULT',
          label: column.label,
        };
        if (column.selected) {
          addFilters.push(filter);
          setLastAddedFilter(filter.fieldName); // Record last added filter so it can be auto opened
        } else {
          removeFilters.push(filter);
          delete queryFilters.current[filter.fieldName];
        }
      } else if (column.filterStyle) {
        updateFilters.push({
          fieldName: column.fieldName,
          type: filterType,
          filterStyle: column.filterStyle,
          label: column.label,
        });
      }
    });
    Analytics.track('Workspace filters updated', {
      screenName: props.screenName,
    });
    dispatch(viewActions.updateFilters(props.screenName, addFilters, removeFilters, updateFilters));
  };

  const handleFilterUpdated = useCallback(
    (filter, newFilter) => {
      queryFilters.current[filter.fieldName] = newFilter;

      if (!props.preventSelectionResetOnFilterChange) {
        workspaceSelection?.onRowSelect?.([], [], true);
      }

      dispatch(viewActions.updateFilter(screenName, filter));
    },
    [dispatch, workspaceSelection, screenName]
  );

  const filterColumns = useFilterColumns(props.availableColumns);

  const { filters, selectedFieldNames } = useMemo(() => {
    const filterElements: Array<ReactElement> = [];
    const selectedFieldNames: Array<string> = [];
    FILTER_TYPES.forEach((filterType) => {
      currentView?.[filterType]?.forEach((filter: ICurrentViewFilter) => {
        if (filter.fieldName === lastAddedFilter) {
          setLastAddedFilter(null);
        }
        selectedFieldNames.push(filter.fieldName);
        if (filter.filterStyle === 'LIST') return;
        filterElements.push(
          <Filter
            key={`filter_${filter.fieldName}_${filter.type}`}
            filter={filter}
            filterType={filterType}
            tabIndex={props.tabIndex}
            filterColumns={filterColumns}
            handleFilterUpdated={handleFilterUpdated}
            dismissFilter={dismissFilter}
          />
        );
      });
    });

    return { filters: filterElements, selectedFieldNames };
  }, [currentView, dismissFilter, filterColumns, handleFilterUpdated, lastAddedFilter, props.tabIndex]);

  //TODO - Move 'GridToolbarWorkspaceRowCounter' to implementing component.
  return (
    <div className={styles.filtersContainer}>
      <div className={styles.filtersSelector}>
        <ColumnSelector
          label="Filters"
          tabIndex={props.tabIndex}
          availableColumns={filterColumns}
          selectedFieldNames={selectedFieldNames}
          allowObjectColumns={true}
          allowImage={false}
          immediateUpdate={true}
          updateColumns={onUpdateColumns}
          data-testid={'filters-container-filter-selector'}
        />
      </div>
      {filters}
    </div>
  );
};

FiltersContainer.defaultProps = {
  includeFilters: true,
};

export default FiltersContainer;
